import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import SearchTabs from './components/SearchTabs/SearchTabs.index';
import KeywordSearch from './components/KeywordSearch/KeywordSearch.index';
import MapSearch from './components/MapSearch/MapSearch.index';
import FilterPanel from './components/FilterPanel/FilterPanel.index';
import ShopListView from './components/ShopListView/ShopListView.index';
import MyPageLayout from '../../components/Layouts/MyPage/MyPageLayout.index';
import * as actions from '../../store/actions/shopList/shopListActions';
import { RootState } from '../../store/reducers';
import { homeStateType } from '../../store/reducers/home/homeReducer';
import { HOME_REQUEST, SCHEDULE_DISPATCH } from '../../store/actions';

const ShopList: FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    searchMode,
    searchParams,
    filters,
    shops,
    totalCount,
    currentLocation,
    selectedShopId,
    isAnnexSelected,
    loading,
    loaded,
    areaOptions,
    facilityOptions,
    serviceOptions,
  } = useSelector((state: RootState) => state.shopList);

  const { homeDatas } = useSelector<RootState, homeStateType>((state) => state.home);

  const pad = (n: number) => n.toString().padStart(2, '0');
  const JST_OFFSET = 9 * 60 * 60 * 1000;
  const getJSTDate = (date?: Date) => {
    const base = date ? date.getTime() : Date.now();
    return new Date(base + JST_OFFSET);
  };
  const targetDate: Date = getJSTDate();
  const yyyy = targetDate.getUTCFullYear();
  const mm = pad(targetDate.getUTCMonth() + 1);
  const dd = pad(targetDate.getUTCDate());
  const todayStr = `${yyyy}${mm}${dd}`;

  useEffect(() => {
    const memberID = sessionStorage.getItem('memberID');
    if (memberID && homeDatas.member_id === 0) {
      dispatch({
        type: HOME_REQUEST,
        memberID,
        history,
      });
    }
  }, [dispatch, history, homeDatas.member_id]);

  useEffect(() => {
    dispatch(actions.fetchAreaOptions());
    dispatch(actions.fetchFilterOptions());
    dispatch(actions.initalShopsList());
    if (searchMode === 'map') {
      dispatch(actions.fetchCurrentLocation());
    }
  }, [dispatch]);

  const handleTabChange = (mode: 'keyword' | 'map') => {
    dispatch(actions.initalShopsList());
    dispatch(actions.setSearchMode(mode));

    if (mode === 'map') {
      dispatch(actions.fetchCurrentLocation());
    }
  };

  const handleKeywordSearch = (params: { area: string; keyword: string }) => {
    dispatch(
      actions.setSearchParams({
        area: params.area,
        keyword: params.keyword.replace(/\s+/g, ' ').trim(),
      }),
    );
    dispatch(actions.fetchShopsRequest());
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    dispatch(actions.setFilters(newFilters));
    dispatch(actions.fetchShopsRequest());
  };

  const handleApplyFilter = () => {
    dispatch(actions.fetchShopsRequest());
  };

  const handleClearFilter = () => {
    dispatch(actions.clearFilters());
    dispatch(actions.fetchShopsRequest());
  };

  const handleBookmark = (shopId: string) => {
    dispatch(actions.toggleBookmark(shopId));
  };

  const handleReserve = (shopId: string, shopName: string, preOpenDay: string) => {
    const clubId = parseInt(shopId, 10);
    const imageDateP = preOpenDay || null;
    dispatch({
      type: SCHEDULE_DISPATCH,
      clubId,
      clubName: shopName,
      imageDate: imageDateP != null && imageDateP > todayStr ? imageDateP : todayStr,
    });

    history.push('/reservation/schedule');
  };

  const handleShopClick = (shopId: string, isAnnex: boolean) => {
    dispatch(actions.setSelectedShop(shopId, isAnnex));
  };

  const getSearchConditionText = () => {
    if (searchMode === 'map') {
      return '現在地から検索';
    }

    if (searchParams.area) {
      const area = areaOptions.find((a) => a.id === searchParams.area);
      if (area) {
        return area.name;
      }
    }

    if (searchParams.keyword) {
      return searchParams.keyword;
    }

    return '';
  };

  return (
    <MyPageLayout>
      <div className="css-fcftey">
        <section className="p-title-area p-title-area--padding-bottom65">
          <p className="c-title u-text-center">
            ご希望の店舗を
            <br className="u-sp-only" />
            選択してください
          </p>
        </section>

        <section
          className="p-content-area p-content-area--width992 p-content-area--padding-x0-b200 u-gap50 u-sp-gap30"
          style={{ maxWidth: '992px', width: '100%', marginInline: 'auto' }}
        >
          <p className="c-read">
            検索条件で選択、指定した項目は
            <br className="u-sp-only" />
            すべてに当てはまる店舗が表示されます。
          </p>

          <div className="p-content-area__column u-gap30 u-sp-gap25">
            <SearchTabs activeTab={searchMode} onTabChange={handleTabChange} />

            {searchMode === 'keyword' && (
              <>
                <KeywordSearch areaOptions={areaOptions} onSearch={handleKeywordSearch} />
                <FilterPanel
                  isMobile
                  facilityOptions={facilityOptions}
                  serviceOptions={serviceOptions}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onApply={handleApplyFilter}
                  onClear={handleClearFilter}
                />

                {loading ? (
                  <div className="text-center">読み込み中...</div>
                ) : loaded ? (
                  <ShopListView
                    shops={shops}
                    searchCondition={getSearchConditionText()}
                    totalCount={totalCount}
                    facilityOptions={facilityOptions}
                    serviceOptions={serviceOptions}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onApplyFilter={handleApplyFilter}
                    onClearFilter={handleClearFilter}
                    onBookmark={handleBookmark}
                    onReserve={handleReserve}
                  />
                ) : (
                  <></>
                )}
              </>
            )}

            {searchMode === 'map' && (
              <>
                <FilterPanel
                  isMobile
                  facilityOptions={facilityOptions}
                  serviceOptions={serviceOptions}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onApply={handleApplyFilter}
                  onClear={handleClearFilter}
                />

                <div className="p-shop-list">
                  <div className="p-black-box">現在地から検索</div>
                  <div className="p-shop-list__inner">
                    <MapSearch
                      shops={shops}
                      currentLocation={currentLocation || undefined}
                      selectedShopId={selectedShopId || undefined}
                      isAnnexSelected={isAnnexSelected}
                      onShopClick={handleShopClick}
                      onBookmark={handleBookmark}
                      onReserve={handleReserve}
                    />

                    <div className="p-shop-list__side-menu">
                      <FilterPanel
                        isMobile={false}
                        facilityOptions={facilityOptions}
                        serviceOptions={serviceOptions}
                        filters={filters}
                        onFilterChange={handleFilterChange}
                        onApply={handleApplyFilter}
                        onClear={handleClearFilter}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </MyPageLayout>
  );
};

export default ShopList;
